import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { EASE, EASE_INOUT } from './motion-kit';
import { useActiveSection } from '../hooks';
import { goToSection } from '../lib/scroll';
import { ThemeSwitcher } from './ThemeSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useI18n } from '../i18n';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { stripLocale } from '../lib/localePath';

const SECTION_IDS = ['home', 'about', 'services', 'work', 'contact'];

export function Wordmark({ size = '1.15rem' }: { size?: string }) {
  const mark = `calc(${size} * 1.35)`;
  return (
    <span
      className="inline-flex items-center gap-2.5"
      style={{ color: 'var(--ink)' }}
    >
      <img
        src="/brand/codex-mark.png"
        alt=""
        width={40}
        height={40}
        decoding="async"
        style={{
          width: mark,
          height: mark,
          objectFit: 'contain',
          flex: 'none',
          borderRadius: 8,
        }}
      />
      <span className="display" style={{ fontSize: size, letterSpacing: '-0.01em' }}>
        Code<span style={{ color: 'var(--accent)' }}>x</span>
        <span style={{ color: 'var(--accent)' }}>.</span>
      </span>
    </span>
  );
}

export function Header() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lp = useLocalizedPath();
  const bare = stripLocale(pathname);
  const isHome = bare === '/';
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeSection = useActiveSection(SECTION_IDS);
  const menuId = useId();
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const nav = useMemo(
    () => [
      { kind: 'section' as const, id: 'about', label: t.nav.about, index: '01' },
      { kind: 'section' as const, id: 'services', label: t.nav.services, index: '02' },
      {
        kind: 'route' as const,
        id: 'work',
        label: t.nav.work,
        index: '03',
        to: lp('/projects'),
        match: '/projects',
      },
      {
        kind: 'route' as const,
        id: 'blog',
        label: t.nav.blog,
        index: '04',
        to: lp('/blog'),
        match: '/blog',
      },
      { kind: 'section' as const, id: 'contact', label: t.nav.contact, index: '05' },
    ],
    [t, lp]
  );

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrolled(window.scrollY > 32);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    closeBtnRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const gotoSection = (id: string) => {
    setMenuOpen(false);
    window.setTimeout(() => goToSection(id, navigate, pathname), menuOpen ? 280 : 0);
  };

  const gotoRoute = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };

  const isItemActive = (item: (typeof nav)[number]) => {
    if (item.kind === 'route') {
      if (item.match === '/projects') {
        return bare.startsWith('/projects') || (isHome && activeSection === 'work');
      }
      return bare.startsWith(item.match);
    }
    return isHome && activeSection === item.id;
  };

  return (
    <>
      <a href={isHome ? '#home' : lp('/')} className="skip-link">
        {t.nav.skip}
      </a>

      <motion.header
        initial={false}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, delay: isHome ? 0.05 : 0, ease: EASE }}
        className={`site-header${scrolled || menuOpen || !isHome ? ' is-solid' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}
      >
        <div
          className="mx-auto flex items-center justify-between gap-4 px-6 md:px-12 site-header__inner"
        >
          <button
            onClick={() => {
              setMenuOpen(false);
              if (isHome) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                history.replaceState(null, '', lp('/'));
              } else {
                navigate(lp('/'));
              }
            }}
            aria-label={t.nav.backToTop}
            data-cursor
            className="nav-link"
          >
            <Wordmark />
          </button>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Primary">
            {nav.map((item) => {
              const active = isItemActive(item);
              return (
                <button
                  key={item.id}
                  onClick={() =>
                    item.kind === 'route' ? gotoRoute(item.to) : gotoSection(item.id)
                  }
                  className="link-line nav-link"
                  aria-current={active ? 'true' : undefined}
                  style={{
                    color: active ? 'var(--ink)' : 'var(--muted)',
                    paddingBottom: 2,
                    fontSize: '0.9rem',
                    fontWeight: active ? 560 : 450,
                  }}
                >
                  {item.label}
                </button>
              );
            })}
            <LanguageSwitcher />
            <ThemeSwitcher />
            <Link
              to={lp('/devis')}
              data-cursor
              className="btn-pill"
              style={{ fontSize: '0.85rem' }}
              onClick={() => setMenuOpen(false)}
            >
              {t.nav.startProject}
            </Link>
          </nav>

          <div className="lg:hidden flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <button
              ref={closeBtnRef}
              className="mono-label menu-toggle"
              style={{ color: 'var(--ink)' }}
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-label={menuOpen ? t.nav.close : t.nav.menu}
              data-cursor
            >
              {menuOpen ? <X size={20} strokeWidth={2} aria-hidden /> : <Menu size={20} strokeWidth={2} aria-hidden />}
              <span className="menu-toggle__label">{menuOpen ? t.nav.close : t.nav.menu}</span>
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id={menuId}
            role="dialog"
            aria-modal="true"
            aria-label={t.nav.menu}
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.55, ease: EASE_INOUT }}
            className="site-menu-glass"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 99,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '88px 24px 48px',
            }}
          >
            <nav aria-label="Mobile">
              {nav.map((item, i) => (
                <div key={item.id} style={{ overflow: 'hidden' }}>
                  <motion.button
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '110%' }}
                    transition={{ duration: 0.6, delay: 0.12 + i * 0.07, ease: EASE }}
                    onClick={() =>
                      item.kind === 'route' ? gotoRoute(item.to) : gotoSection(item.id)
                    }
                    className="display py-3 w-full"
                    style={{
                      fontSize: 'clamp(2rem, 8vw, 3.6rem)',
                      color: isItemActive(item) ? 'var(--ink)' : 'var(--muted)',
                      textAlign: 'start',
                    }}
                  >
                    {item.label}
                  </motion.button>
                </div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.45 }}
              className="mt-12 flex flex-col gap-5"
            >
              <div className="flex flex-wrap items-center gap-3">
                <LanguageSwitcher />
                <ThemeSwitcher />
              </div>
              <Link
                to={lp('/devis')}
                data-cursor
                className="btn-pill w-max"
                style={{ fontSize: '0.9rem' }}
                onClick={() => setMenuOpen(false)}
              >
                {t.nav.startProject}
              </Link>
              <a
                href="mailto:contact@codexalgerie.com"
                className="mono-label link-line w-max"
                style={{ color: 'var(--ink)' }}
              >
                contact@codexalgerie.com
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
